import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { entrarComGoogle } from "../firebase.js";
import { loginGoogleMedido, mensagemErroLoginGoogle } from "../login-google.js";
import { Icone, Marca, MarcaTexto } from "../componentes/Marca.js";
import { BairroRodape } from "../componentes/BairroRodape.js";
import { RodapeLegal } from "../componentes/Legal.js";
import { Estrelas } from "../componentes/Retrato.js";
import type { ReactNode } from "react";
import { Aviso } from "../componentes/ui.js";
import {
  formatBRL, reais,
} from "@ida/core";
import { ofertaLanding, type CicloPreco } from "./precos-landing.js";
import { EH_ISCA, NOME_MARCA, URL_APP } from "../marca.js";
import { evento } from "../telemetria.js";
import { MetadadosPagina } from "../componentes/Metadados.js";

function ValorPlano({ soma, ciclo }: { soma: number; ciclo: CicloPreco }) {
  const oferta = ofertaLanding(reais(soma), ciclo);
  return (
    <div className="lp__preco-oferta" data-preco-oferta={soma === 0 ? "simples" : "premium"}
      data-ciclo={ciclo} aria-live="polite" aria-atomic="true">
      <div className="lp__preco-comparacao">
        <span className="lp__preco-ancora" data-preco-ancora>{oferta.ancora}</span>
        <span className="lp__preco-economia" data-preco-economia>{oferta.economia}</span>
      </div>
      <div className="lp__preco-valor">
        <strong data-preco-valor>{formatBRL(oferta.valor)}</strong>
        <span>/mês</span>
      </div>
      <p className="lp__preco-cobranca" data-preco-cobranca>{oferta.cobranca}</p>
    </div>
  );
}

/** O seletor único também controla o preço de todas as faixas do simulador. */
function CalculadoraPreco({ botao, ciclo }: { botao: ReactNode; ciclo: CicloPreco }) {
  const [soma, setSoma] = useState(8000);
  const TETO = 210_000;
  const posicao = ((soma - 5000) / (TETO - 5000)) * 100;
  return (
    <div className="lp__preco-cartao lp__preco-cartao--premium">
      <span className="rotulo">Aluguéis a partir de R$ 5 mil por mês</span>
      <ValorPlano soma={soma} ciclo={ciclo} />
      <p className="lp__preco-faixa">
        Arraste até a soma dos seus contratos:{" "}
        <strong>
          {soma >= TETO ? "acima de R$ 200 mil" : `R$ ${soma.toLocaleString("pt-BR")}`}
        </strong>{" "}
        por mês.
      </p>
      <input className="lp__slider" type="range" min={5000} max={TETO} step={1000}
        value={soma} onChange={(e) => setSoma(Number(e.target.value))}
        style={{ background: `linear-gradient(to right, var(--acento) ${posicao}%, var(--linha) ${posicao}%)` }}
        aria-label="Soma mensal dos seus contratos de aluguel" />
      <div className="lp__escala">
        <span>R$ 5 mil</span>
        <span>R$ 200 mil+</span>
      </div>
      <span className="lp__preco-mais">Tudo do plano básico, e mais:</span>
      <ul className="lp__preco-lista">
        <li>DARF oficial do imposto do aluguel emitido todo mês, para pagar no seu banco ou por Pix intermediado</li>
        <li>Lembretes do imposto por WhatsApp e email até a guia paga</li>
        <li>Regularização dos meses em aberto do ano</li>
        <li>Guia, comprovante ou relatório de qualquer mês, na hora que pedir</li>
      </ul>
      {botao}
    </div>
  );
}
import "./landing.css";
import { iniciarInteracoesLanding } from "./landing-interacoes.js";

/**
 * Landing publica. A venda e feita em cima do risco real de nao recolher
 * (numeros verdadeiros, nada de urgencia inventada) e da solucao de uma
 * mensagem por mes. O botao de entrar aparece no hero e no fim.
 */
function IconeGoogle({ tamanho = 18 }: { tamanho?: number }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** O celular de WhatsApp da landing: o produto de verdade, em CSS. */
function PalcoZap() {
  return (
          <div className="lp__palco" role="group" aria-label="Demonstração de conversa com dados ilustrativos">
            <div className="lp__chip lp__chip--1">
              <span className="lp__chip-alerta">!</span>
              <span>Vence dia 30/09<small>DARF de agosto · R$ 394,54</small></span>
            </div>
            <div className="lp__fone">
              <div className="lp__zap">
                <div className="lp__zap-topo">
                  <span className="lp__zap-voltar" aria-hidden="true" />
                  <span className="lp__zap-foto"><Icone tamanho={22} /></span>
                  <span className="lp__zap-quem">
                    {NOME_MARCA}
                    <small>online</small>
                  </span>
                </div>

                <div className="lp__chat">
                  <span className="lp__chat-hora">1º DE SETEMBRO</span>
                  <div className="lp__balao lp__balao--voce lp__balao--anexo">
                    <em>Encaminhada</em>
                    <span className="lp__anexo">
                      Comprovante Pix
                      <small>PDF · 84 kB</small>
                    </span>
                    <time>09:12<i aria-hidden="true">✓✓</i></time>
                  </div>
                  <div className="lp__balao lp__balao--eles">
                    {"Anotei R$ 3.500 do Centro, em 05/08.\nFalta a Vila Mariana. Quanto entrou?"}
                    <time>09:12</time>
                  </div>
                  <div className="lp__balao lp__balao--voce">
                    2.500<time>09:13<i aria-hidden="true">✓✓</i></time>
                  </div>
                  <div className="lp__balao lp__balao--eles">
                    {"Total de agosto: R$ 6.000,00\nImposto a pagar: R$ 394,54\nVencimento: 30/09\n\nConfirma?"}
                    <time>09:13</time>
                  </div>
                  <div className="lp__balao lp__balao--voce">
                    sim<time>09:14<i aria-hidden="true">✓✓</i></time>
                  </div>
                  <div className="lp__balao lp__balao--eles lp__balao--guia">
                    <strong className="lp__guia-titulo">Guia do imposto sobre aluguel pronta.</strong>
                    <span className="lp__guia-anexo">
                      <i>PDF</i>
                      <span>
                        DARF de agosto
                        <small>R$ 394,54 · vence 30/09</small>
                      </span>
                    </span>
                    <span className="lp__pix-legenda">
                      Pague no seu banco ou use o Pix Copia e Cola:
                    </span>
                    <span className="lp__pix-copia">
                      <span className="lp__pix-toast" data-demo-colar hidden role="status">Colar</span>
                      <code>00020126...BR.GOV.BCB.PIX...6304</code>
                      <b>Pix</b>
                    </span>
                    <time>09:14</time>
                  </div>
                  <div className="lp__balao lp__balao--eles lp__balao--guia lp__balao--pago" hidden>
                    <strong>Imposto sobre aluguel pago</strong>
                    <span>Seu DARF de agosto foi quitado. O comprovante está guardado junto ao mês.</span>
                    <span className="lp__guia-anexo"><i>PDF</i><span>Comprovante de pagamento<small>Agosto/2026 · R$ 394,54</small></span></span>
                    <time>09:16</time>
                  </div>
                </div>

                <div className="lp__zap-barra">
                  <span className="lp__demo-anexo" aria-hidden="true">＋</span>
                  <input className="lp__demo-campo" data-demo-campo type="text" readOnly
                    aria-label="Mensagem de exemplo preenchida automaticamente" placeholder="Mensagem" />
                  <button type="button" className="lp__demo-enviar" data-demo-play aria-label="Enviar mensagem de exemplo">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 3.5 22 12 3 20.5v-7l12-1.5-12-1.5z" /></svg>
                  </button>
                </div>
                <p className="lp__demo-status lp__somente-leitor" data-demo-status role="status" aria-live="polite">Conversa ilustrativa. Nenhum dado é enviado.</p>
              </div>
              <div className="lp__ios-home" data-demo-ios hidden aria-label="Tela inicial do iPhone">
                <div className="lp__ios-status"><strong>9:41</strong><span aria-hidden="true">▮▮▮ ▰</span></div>
                <div className="lp__ios-data">Terça-feira, 1 de setembro<strong>09:41</strong></div>
                <div className="lp__ios-apps">
                  <div><span className="lp__ios-icone lp__ios-icone--zap"><svg width="44" height="44" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M7 25 4 28l1-7a12 12 0 1 1 4 5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M11 9c-3 2 0 8 4 11s7 1 7-1l-4-2-2 1c-2-1-3-3-4-5l1-1Z" fill="currentColor" /></svg></span><small>WhatsApp</small></div>
                  <div><span className="lp__ios-icone lp__ios-icone--banco">◈</span><small>Seu banco</small></div>
                </div>
                <div className="lp__ios-dock" aria-hidden="true"><span>☎</span><span>✉</span><span>◎</span><span>♫</span></div>
                <span className="lp__ios-homebar" aria-hidden="true" />
              </div>
              <div className="lp__banco-demo" data-demo-banco hidden role="group" aria-label="Simulação de Pix no banco">
                <div className="lp__banco-topo"><span aria-hidden="true">◈</span><strong>Seu banco</strong></div>
                <div className="lp__banco-conteudo" data-demo-banco-form>
                  <h3>Pix Copia e Cola</h3>
                  <p>Cole o código que recebeu na conversa.</p>
                  <label>Código Pix<input type="text" readOnly data-demo-pix placeholder="Cole o código aqui" /></label>
                  <div className="lp__banco-resumo"><span>Você vai pagar<strong>R$ 394,54</strong></span><span>Para<strong>Alugando.com</strong></span><small>Pagamento do DARF de agosto</small></div>
                  <button type="button" data-demo-pagar>Pagar</button>
                </div>
                <div className="lp__banco-sucesso" data-demo-banco-sucesso hidden role="status">
                  <span aria-hidden="true">✓</span><h3>Pix enviado</h3><strong>R$ 394,54</strong><p>Voltando para a conversa…</p>
                </div>
              </div>
            </div>
            <div className="lp__chip lp__chip--2">
              <span className="lp__chip-ok">✓</span>
              <span>Comprovante guardado<small>pagamento do imposto · agosto/2026</small></span>
            </div>
          </div>
  );
}



export function Login() {
  const [cicloPreco, setCicloPreco] = useState<CicloPreco>("anual");
  const landingRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (landingRef.current) return iniciarInteracoesLanding(landingRef.current);
  }, []);
  const [erro, setErro] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);
  const tentativaEmCurso = useRef(false);
  const ctaLogin = useRef("link_entrada");
  // Popup intermediario de entrada: termos e Google num lugar so, em vez de
  // repetir o aviso legal debaixo de cada botao da home.
  const [modal, setModal] = useState(false);
  const landingEstatica = typeof window === "undefined";

  async function entrar() {
    if (tentativaEmCurso.current) return;
    tentativaEmCurso.current = true;
    setEntrando(true); setErro(null);
    try {
      await loginGoogleMedido(entrarComGoogle, ctaLogin.current);
    } catch (e) {
      setErro(mensagemErroLoginGoogle(e));
    } finally { tentativaEmCurso.current = false; setEntrando(false); }
  }

  /*
   * Na fachada-isca (impostodoaluguel pos-virada), todo CTA leva o lead para
   * a casa do produto; ?entrar=1 abre o popup de login la do outro lado.
   */
  const BotaoEntrar = ({
    rotulo = "Começar grátis", google = true, compacto = false, local,
  }: { rotulo?: string; google?: boolean; compacto?: boolean; local: string }) => {
    const conteudo = google
      ? <>
          <span className="botao-google__icone"><IconeGoogle /></span>
          <span>{rotulo}</span>
        </>
      : rotulo;
    const classe = google
      ? [
          "botao", "botao--google",
          compacto ? "botao--google-compacto" : "",
        ].filter(Boolean).join(" ")
      : "botao";
    // O HTML da home e publicado sem o bundle do aplicativo. No build do
    // servidor, os CTAs viram links normais para /painel; dentro do app eles
    // continuam abrindo o modal de entrada sem recarregar a pagina.
    return EH_ISCA || landingEstatica
      ? <a className={classe} data-cta-id={local} href={`${URL_APP}?entrar=1`}>{conteudo}</a>
      : <button className={classe} data-cta-id={local}
          onClick={() => { ctaLogin.current = local; setErro(null); evento("cta_landing_clicado", { cta_id: local, rotulo_cta: rotulo }); evento("abriu_login", { rotulo, cta_id: local }); setModal(true); }}>
          {conteudo}
        </button>;
  };

  // Chegou da isca com ?entrar=1: abre o popup de login direto.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("entrar")) {
      evento("abriu_login", { origem: "cta_landing" });
      setModal(true);
    }
  }, []);

  return (
    <>
      <MetadadosPagina caminho="/" />
      <header className="lp-nav">
        <div className="lp-nav__interno">
          <Marca />
          <nav aria-label="Navegação principal">
            <a href="#como-funciona">Como funciona</a>
            <a href="#precos">Planos e preços</a>
            <a href="#faq">Dúvidas</a>
          </nav>
          <BotaoEntrar rotulo="Entrar" local="cabecalho_entrar" compacto />
        </div>
      </header>
    <main className="lp" ref={landingRef}>
      {/* ------------------------------------------------------------ hero */}
      <section className="lp__hero">
        <div className="lp__interno lp__hero-grid">
          <div className="pilha pilha-5 lp__hero-texto">
            <h1>Você aluga.<br />
              <span className="lp__grifo">O resto é com a gente.</span></h1>
            <p className="lede">
              O <strong>alugando.com</strong> cobra o inquilino, acompanha os
              recebimentos e cuida do imposto todos os meses, antes que o
              atraso vire multa.
            </p>
            <div className="pilha pilha-3">
              <div className="lp__cta-linha">
                <BotaoEntrar local="hero_comecar" />
                <a className="lp__link-hero" href="#como-funciona">Veja como funciona <span aria-hidden="true">↗</span></a>
              </div>
              <p className="lp__confianca">
                Primeiro mês grátis · A partir de R$ 34,90 por mês no anual
              </p>
            </div>
          </div>
          <div className="lp__palco-moldura">
            <PalcoZap />
          </div>
        </div>
        <div className="lp__hero-servicos" aria-label="O que o alugando.com cuida">
          <span><i aria-hidden="true">↗</i> Cobrança do inquilino</span>
          <span><i aria-hidden="true">✓</i> Controle dos recebimentos</span>
          <span><i aria-hidden="true">≡</i> Imposto em dia</span>
          <span><i aria-hidden="true">↗</i> Tudo na conversa</span>
        </div>
      </section>

      {/* ------------------------------------------------------- depoimento
          Logo depois do hero: quem chega ouve primeiro uma locadora de
          verdade, antes de ler qualquer lista de recursos. */}
      <section className="lp__prova">
        <div className="lp__interno">
          <p className="lp__prova-rotulo">
            Proprietários de todo o Brasil já confiam no alugando.com
          </p>
          <figure className="depo">
            <blockquote>
              Tenho três imóveis alugados e nunca tinha certeza de nada: se
              todo mundo tinha pago, se o imposto do mês estava certo, se eu
              já tinha pago a guia. Pela primeira vez eu sei exatamente onde
              estou com cada imóvel.
            </blockquote>
            <figcaption className="depo__quem">
              <img
                className="depo__foto"
                src="/fotos/mirian.jpg?v=2"
                alt=""
                width="56"
                height="56"
                loading="lazy"
                decoding="async"
              />
              <span className="depo__nome">
                <strong>Mirian Silva</strong>
                <small>3 imóveis alugados</small>
              </span>
              <span className="depo__nota">
                <Estrelas nota={4.9} />
                <span>4,9 de 5</span>
              </span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ---------------------------------------------------------- fotos
          Faixa de fotos entre o depoimento e os pilares: imoveis e gente em
          casa, so ambiente. Decorativas, por isso alt vazio. */}
      <section className="lp__fotos" aria-hidden="true">
        <div className="lp__fotos-trilho">
          <img src="/fotos/casa-azul.webp" alt="" width="900" height="600" loading="lazy" />
          <img src="/fotos/senhora-cozinha.webp" alt="" width="900" height="506" loading="lazy" />
          <img src="/fotos/chaves-porta.webp" alt="" width="900" height="600" loading="lazy" />
          <img src="/fotos/casa-palmeiras.webp" alt="" width="560" height="840" loading="lazy" />
          <img src="/fotos/cozinha-cafe-celular.webp" alt="" width="900" height="506" loading="lazy" />
          <img src="/fotos/sobrados-telhado.webp" alt="" width="900" height="600" loading="lazy" />
        </div>
      </section>

      <section className="lp__modalidades" id="como-funciona" aria-labelledby="modalidades-titulo">
        <div className="lp__interno">
          <div className="lp__modalidades-cabecalho">
            <h2 className="lp__titulo" id="modalidades-titulo">Com imobiliária ou<br />direto com o inquilino.</h2>
          </div>
          <div className="lp__modalidades-opcoes">
            <div className="lp__modalidade lp__modalidade--imobiliaria">
              <div className="lp__modalidade-topo">
                <span>COM ADMINISTRADORA</span>
                <svg viewBox="0 0 64 64" fill="none" aria-hidden="true"><path d="M12 54V18L32 10v44M32 26l20-8v36M7 54h50"/><path d="M20 24v4m0 8v4m12 5h9v9m1-24v4m0 7v3"/></svg>
              </div>
              <h3>Já aluga pelo QuintoAndar<br />ou por uma imobiliária?</h3>
              <p><strong>O Alugando também é útil para você.</strong> Organizamos seus recebimentos e comprovantes e calculamos o imposto dos aluguéis considerando as despesas informadas. Ajudamos você a manter o imposto em dia e evitar multas por atraso no pagamento.</p>
            </div>
            <div className="lp__modalidade lp__modalidade--direto">
              <div className="lp__modalidade-topo">
                <span>DIRETO COM O INQUILINO</span>
                <svg viewBox="0 0 64 64" fill="none" aria-hidden="true"><path d="m8 30 24-20 24 20M15 25v29h34V25M27 54V37h10v17"/><path d="M28 24h8"/></svg>
              </div>
              <h3>Aluga direto<br />com o inquilino?</h3>
              <p>Além de organizar seus recebimentos, comprovantes e gerar guia do imposto dos aluguéis, você também conta com <strong>cobrança e lembretes de pagamento.</strong></p>
            </div>
          </div>
          <p className="lp__modalidades-nota"><span aria-hidden="true">✓</span><span><strong>Você pode continuar com sua imobiliária ou alugar direto.</strong> Ajudamos você a organizar os aluguéis, evitar multas e declarar o imposto corretamente.</span></p>
        </div>
      </section>

      {/* --------------------------------------------------------- pilares */}
      <section className="lp__recursos">
        <div className="lp__interno">
          <h2 className="lp__titulo lp__titulo--uma-linha lp__titulo--antes-passos">Todo aluguel dá trabalho. O seu deixa de dar.</h2>
          <div className="lp__passos">
            <div className="lp__passo">
              <h3>Cobrança sem constrangimento</h3>
              <p>
                Lembretes educados por WhatsApp e email ao inquilino (ou à
                imobiliária), antes e depois do vencimento, sempre em seu nome.
              </p>
            </div>
            <div className="lp__passo">
              <h3>Quem pagou, num relance</h3>
              <p>
                A régua do ano mostra cada imóvel como pago, parcial ou em
                aberto, com comprovante guardado e recibo em um clique.
              </p>
            </div>
            <div className="lp__passo lp__passo--destaque">
              <h3>Imposto no automático</h3>
              <p>
                O carnê-leão é calculado todo mês, a guia oficial sai emitida
                pelo Sicalc e dá até para pagar por Pix. Em janeiro, um
                arquivo preenche sua declaração.
              </p>
            </div>
            <div className="lp__passo">
              <h3>Tudo por WhatsApp ou email</h3>
              <p>
                Encaminhe o comprovante do jeito que chegou. O sistema lê,
                lança no mês certo e pergunta o que faltar. Sem planilha, sem
                formulário.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- regua do ano */}
      <section className="lp__controle">
        <div className="lp__interno lp__controle-grid">
          <div className="pilha pilha-3">
            <h2 className="lp__titulo">O ano de cada imóvel, numa régua.</h2>
            <p className="lp__sub">
              Cada comprovante que você encaminha vira um mês pago. Atrasou,
              o sistema lembra o inquilino em seu nome e o mês fica marcado
              em aberto. Pagou, o recibo sai em um clique. Você abre e sabe
              quanto entrou no ano, imóvel por imóvel.
            </p>
          </div>
          <div className="lp__palco-regua" role="group" aria-label="Exemplo interativo de controle anual">
            <div className="lp__regua-cartao">
              <img className="lp__regua-capa" src="/fotos/casa-azul.webp" alt=""
                width="900" height="600" loading="lazy" />
              <div className="lp__regua-topo">
                <strong>Vila Mariana · casa 72</strong>
                <span>Inquilina Marina · R$ 2.500 · vence dia 5</span>
              </div>
              <div className="lp__regua-meses">
                <button type="button" className="lp__mes lp__mes--pago" data-mes="jan" data-detalhe="Janeiro: R$ 2.500 recebidos. Comprovante guardado e recibo disponível." aria-label="Janeiro" aria-pressed={false}>jan</button>
                <button type="button" className="lp__mes lp__mes--pago" data-mes="fev" data-detalhe="Fevereiro: R$ 2.500 recebidos. Comprovante guardado e recibo disponível." aria-label="Fevereiro" aria-pressed={false}>fev</button>
                <button type="button" className="lp__mes lp__mes--pago" data-mes="mar" data-detalhe="Março: R$ 2.500 recebidos. Comprovante guardado e recibo disponível." aria-label="Março" aria-pressed={false}>mar</button>
                <button type="button" className="lp__mes lp__mes--pago" data-mes="abr" data-detalhe="Abril: R$ 2.500 recebidos. Comprovante guardado e recibo disponível." aria-label="Abril" aria-pressed={false}>abr</button>
                <button type="button" className="lp__mes lp__mes--pago" data-mes="mai" data-detalhe="Maio: R$ 2.500 recebidos. Comprovante guardado e recibo disponível." aria-label="Maio" aria-pressed={false}>mai</button>
                <button type="button" className="lp__mes lp__mes--pago" data-mes="jun" data-detalhe="Junho: R$ 2.500 recebidos. Comprovante guardado e recibo disponível." aria-label="Junho" aria-pressed={false}>jun</button>
                <button type="button" className="lp__mes lp__mes--parcial" data-mes="jul" data-detalhe="Julho: R$ 1.250 recebidos de R$ 2.500. Pagamento parcial; recibo emitido." aria-label="Julho" aria-pressed={false}>jul</button>
                <button type="button" className="lp__mes lp__mes--aberto" data-mes="ago" data-detalhe="Agosto: R$ 2.500 em aberto. Lembrete enviado à inquilina." aria-label="Agosto" aria-pressed={true}>ago</button>
                <button type="button" className="lp__mes lp__mes--futuro" data-mes="set" data-detalhe="Setembro: mês ainda não fechado. O recebimento aparecerá aqui." aria-label="Setembro" aria-pressed={false}>set</button>
                <button type="button" className="lp__mes lp__mes--futuro" data-mes="out" data-detalhe="Outubro: mês ainda não fechado. O recebimento aparecerá aqui." aria-label="Outubro" aria-pressed={false}>out</button>
                <button type="button" className="lp__mes lp__mes--futuro" data-mes="nov" data-detalhe="Novembro: mês ainda não fechado. O recebimento aparecerá aqui." aria-label="Novembro" aria-pressed={false}>nov</button>
                <button type="button" className="lp__mes lp__mes--futuro" data-mes="dez" data-detalhe="Dezembro: mês ainda não fechado. O recebimento aparecerá aqui." aria-label="Dezembro" aria-pressed={false}>dez</button>
              </div>
              <p className="lp__mes-detalhe" data-mes-detalhe role="status" aria-live="polite">Agosto: R$ 2.500 em aberto. Lembrete enviado à inquilina.</p>
              <small className="lp__demo-nota">Exemplo ilustrativo · selecione um mês</small>
              <div className="lp__regua-legenda">
                <span className="lp__leg lp__leg--pago">pago</span>
                <span className="lp__leg lp__leg--parcial">parcial</span>
                <span className="lp__leg lp__leg--aberto">em aberto</span>
              </div>
              <div className="lp__regua-linha">
                <span>Recebido no ano</span>
                <strong>R$ 16.250,00</strong>
              </div>
            </div>
            <div className="lp__chip lp__chip--regua1">
              <span className="lp__chip-alerta">!</span>
              <span>Agosto em aberto<small>lembrete enviado à inquilina</small></span>
            </div>
            <div className="lp__chip lp__chip--regua2">
              <span className="lp__chip-ok">✓</span>
              <span>Recibo de julho emitido<small>enviado em um clique</small></span>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- o custo de nao */}
      <section className="lp__risco">
        <div className="lp__interno">
          <h2 className="lp__titulo lp__titulo--uma-linha">Imposto atrasado fica mais caro a cada dia.</h2>
          <p className="lp__sub">
            Deixar o carnê-leão do aluguel para depois transforma um
            esquecimento em multa e juros. Manter cada mês em dia é mais
            simples e muito mais barato do que regularizar tudo de uma vez.
          </p>
          <div className="lp__stats">
            <div className="lp__stat lp__stat--ruim">
              <strong>0,33%</strong>
              <span>de multa por dia de atraso do imposto do aluguel, a partir
              do primeiro dia útil após o vencimento</span>
            </div>
            <div className="lp__stat lp__stat--ruim">
              <strong>20% + Selic</strong>
              <span>a multa bate o teto em ~2 meses; daí em diante os juros
              continuam correndo</span>
            </div>
            <div className="lp__stat lp__stat--ruim">
              <strong>50%</strong>
              <span>é a multa isolada prevista em lei para carnê-leão de
              aluguel não recolhido, se a Receita cobrar primeiro. Regularizar
              antes evita essa</span>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ a guia */}
      <section className="lp__guia">
        <div className="lp__interno lp__lado-a-lado">
          <div>
            <h2 className="lp__titulo">A guia chega pronta. O comprovante fica guardado.</h2>
            <p className="lp__sub">
              Havendo imposto, o DARF oficial chega emitido pelo Sicalc, com os
              acréscimos calculados pela própria Receita. Na hora de pagar,
              você escolhe: <strong>paga a guia no seu banco</strong>, digitando
              os dados em Pagamentos &gt; DARF, ou <strong>pede um código
              Pix</strong> e a gente quita o DARF na Receita por você, em seu
              CPF, devolvendo o comprovante na conversa.
            </p>
          </div>
          <div className="lp__darf" aria-hidden="true">
            <span className="lp__carimbo">Paga</span>
            <div className="lp__darf-cab">
              <strong>DARF</strong>
              <span className="tenue">código 0190 · carnê-leão</span>
            </div>
            <div className="lp__darf-linha">
              <span>Período de apuração</span><span>08/2026</span>
            </div>
            <div className="lp__darf-linha">
              <span>Vencimento</span><span>30/09/2026</span>
            </div>
            <div className="lp__darf-linha">
              <span>CPF do contribuinte</span><span>•••.982.247-••</span>
            </div>
            <div className="lp__darf-linha lp__darf-linha--total">
              <span>Valor a pagar</span><span>R$ 394,54</span>
            </div>
            <div className="lp__darf-codigo" />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- confianca */}
      {/* ----------------------------------------------------------- preco */}
      <section className="lp__preco" id="precos">
        <div className="lp__interno lp__preco-vitrine">
          <div className="lp__preco-lado pilha pilha-2">
            <h2 className="lp__titulo">Preço pelo tamanho dos seus aluguéis.</h2>
            <p className="lp__sub">
              Primeiro mês grátis. Escolha o anual para economizar ou o
              mensal sem fidelidade.
            </p>
            <div className="lp__preco-periodicidade">
              <label className="lp__preco-switch">
                <span>Mensal</span>
                <input type="checkbox" role="switch" data-preco-ciclo
                  aria-label="Plano anual com desconto" checked={cicloPreco === "anual"}
                  onChange={(e) => setCicloPreco(e.target.checked ? "anual" : "mensal")} />
                <span className="lp__preco-switch-trilho" aria-hidden="true" />
                <span>Anual <small>com desconto</small></span>
              </label>
            </div>
          </div>
          <div className="lp__precos">
            <div className="lp__preco-cartao lp__preco-cartao--simples">
              <span className="rotulo">Aluguéis abaixo de R$ 5 mil por mês</span>
              <ValorPlano soma={0} ciclo={cicloPreco} />
              <ul className="lp__preco-lista">
                <li>Lembrete de cobrança para o inquilino perto do vencimento</li>
                <li>Recibo de aluguel na hora</li>
                <li>Controle de quem pagou e quem atrasou, imóvel por imóvel</li>
                <li>Escrituração fiscal de todos os meses em dia</li>
                <li>Arquivo de janeiro que preenche sua declaração</li>
                <li>Converse com o alugando.com</li>
              </ul>
              <BotaoEntrar local="plano_basico_comecar" />
            </div>
            <CalculadoraPreco ciclo={cicloPreco} botao={<BotaoEntrar local="calculadora_comecar" />} />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- faq */}
      <section className="lp__duvidas">
        <div className="lp__interno lp__faq-grid" id="faq">
          <div className="lp__faq-lado">
            <h2 className="lp__titulo">O que todo mundo pergunta</h2>
            <p className="lp__sub">
              Se a sua dúvida não estiver aqui, pergunte na conversa depois de
              entrar. É o mesmo sistema que cuida dos seus meses.
            </p>
            <img className="lp__faq-foto" src="/fotos/sofa-celular.webp" alt=""
              width="1100" height="619" loading="lazy" />
          </div>
          <div className="lp__faq">
            <details>
              <summary>Vocês substituem minha imobiliária ou o QuintoAndar?</summary>
              <p>Não. Você continua com sua imobiliária ou com o QuintoAndar. O Alugando organiza seus recebimentos e comprovantes e calcula o imposto dos aluguéis considerando as despesas informadas. Se você aluga direto com o inquilino, também conta com cobrança e lembretes de pagamento.</p>
            </details>
            <details>
              <summary>Como funciona a cobrança do inquilino?</summary>
              <p>
                Você ativa quando quiser, imóvel por imóvel: perto do
                vencimento, o sistema lembra o inquilino por WhatsApp ou email,
                com o valor e a sua chave Pix. O aluguel cai direto na sua
                conta, como sempre caiu: dinheiro de aluguel nunca passa pela
                gente. Você encaminha o comprovante, o sistema dá baixa no mês
                certo e emite o recibo na hora, se o inquilino pedir. Prefere
                continuar cobrando você mesmo? Todo o restante funciona igual.
              </p>
            </details>
            <details>
              <summary>Como eu mando as informações?</summary>
              <p>
                Do jeito que elas chegam até você: encaminhe o Pix, o print, o
                PDF do banco, a foto do recibo, ou escreva só o valor. Nosso
                sistema lê, entende o que é aluguel e o que é imposto pago, e
                pergunta o que faltou. Não existe formulário, planilha nem
                formato certo, e se você preferir digitar tudo na mão, o site
                também está lá.
              </p>
            </details>
            <details>
              <summary>E se eu esquecer de mandar alguma coisa?</summary>
              <p>
                O sistema sabe quais imóveis você tem e percebe o que não
                chegou. Ele pergunta, e continua lembrando enquanto o mês não
                fecha. Se ainda assim ficou um buraco no ano, dá para
                regularizar depois, com o cálculo de multa e juros mês a mês.
              </p>
            </details>
            <details>
              <summary>Preciso guardar os comprovantes em algum lugar?</summary>
              <p>
                Não. Tudo que passa pela conversa fica arquivado no mês certo,
                por imóvel e por CPF de inquilino. Quando precisar, peça no
                WhatsApp: a guia de março, o comprovante de junho ou o
                relatório do ano inteiro chegam em segundos.
              </p>
            </details>
            <details>
              <summary>Como eu pago a guia do imposto sobre aluguel?</summary>
              <p>
                Do jeito que você preferir, e a escolha é sua a cada mês.
                Opção um: a guia oficial (DARF) chega na conversa e você paga
                no seu banco, digitando os dados em Pagamentos &gt; DARF, como
                em qualquer banco tradicional. Opção dois: você responde{" "}
                <em>pix</em>, recebe um código de qualquer banco (inclusive
                digitais, que não pagam DARF direto) e o sistema quita o DARF
                na Receita em seu CPF automaticamente. O Pix vai para
                Alugando.com, CNPJ 40.838.857/0001-08, a empresa deste site. O
                comprovante bancário chega na conversa em seguida e o
                pagamento fica visível no seu e-CAC. Se o DARF não sair por
                qualquer motivo, o valor volta integral pela devolução do
                próprio Pix.
              </p>
            </details>
            <details>
              <summary>Estou atrasado há meses. E agora?</summary>
              <p>
                Regularizar por conta própria é muito mais barato do que
                esperar: a multa de mora tem teto de 20%, e pagar antes de
                qualquer fiscalização evita a multa isolada de 50% prevista
                para o carnê-leão não recolhido. A gente calcula cada mês em
                aberto e te entrega as guias.
              </p>
            </details>
            <details>
              <summary>Meu imóvel é administrado por imobiliária.</summary>
              <p>
                Não muda o imposto: se quem aluga de você (o locatário do
                contrato) é pessoa física, o carnê-leão é seu, mesmo que o
                dinheiro passe pela imobiliária
                antes de chegar na sua conta. Muda só o que você me manda, e
                qualquer coisa serve: o Pix do inquilino, o comprovante de
                repasse ou o extrato mensal que a imobiliária te envia. A taxa
                de administração ainda entra como despesa do imóvel, junto com
                IPTU e condomínio que você paga, e reduz o rendimento antes do
                cálculo.
              </p>
            </details>
            <details>
              <summary>Meu inquilino é uma empresa.</summary>
              <p>
                Então não existe carnê-leão para esse imóvel: a empresa retém o
                imposto na fonte. O sistema te avisa isso no cadastro em vez de
                te cobrar por algo que você não deve.
              </p>
            </details>
            <details>
              <summary>Meu aluguel é menor que R$ 5.000. Preciso disso?</summary>
              <p>
                Sim. Mesmo sem imposto a pagar no mês, o Alugando ajuda a
                cobrar o inquilino, acompanhar recebimentos, guardar
                comprovantes e emitir recibos. Em 2026, se o total dos seus
                rendimentos tributáveis sujeitos ao carnê-leão no mês não
                ultrapassar R$ 5.000, a redução zera esse imposto mensal.
                Some os aluguéis aos demais rendimentos sujeitos ao
                carnê-leão, como serviços prestados a pessoas físicas e
                rendimentos tributáveis do exterior. Rendimentos tributados
                na fonte, como salários, não entram nesse cálculo mensal. Já
                a declaração anual considera os rendimentos sujeitos ao
                ajuste e pode resultar em imposto adicional: para os
                recebimentos de 2026, o ajuste é na declaração de 2027.
              </p>
            </details>
            <details>
              <summary>Que imposto exatamente vocês cuidam?</summary>
              <p>
                O IRPF recolhido via carnê-leão sobre aluguéis recebidos de
                pessoa física, e só isso. Aluguel pago por empresa tem retenção
                na fonte feita pela própria empresa e fica fora. Os novos
                IBS/CBS da reforma tributária, que a partir de 2027 podem
                alcançar alguns proprietários com muitos imóveis e receita alta,
                também estão fora do escopo hoje; se a regra te alcançar, a
                gente avisa antes.
              </p>
            </details>
            <details>
              <summary>Preciso de contador?</summary>
              <p>
                Para o carnê-leão mensal de aluguel simples, não: o cálculo é
                objetivo e você confirma cada valor antes de emitir. Casos
                complexos (espólio, imóvel no exterior, muitos coproprietários)
                a gente reconhece e indica um profissional em vez de fingir que
                resolve.
              </p>
            </details>
            <details>
              <summary>Vocês entram na minha conta da Receita?</summary>
              <p>
                Não. Sem procuração, sem senha do gov.br. Calculamos a partir
                do que você informa, geramos a guia e o arquivo anual. Quem
                importa e declara é você, na sua conta, com tudo pronto.
              </p>
            </details>
            <details>
              <summary>Quanto custa?</summary>
              <p>
                Depende do tamanho dos seus contratos. Abaixo de R$ 5 mil de
                aluguéis, o anual sai por R$ 34,90 por mês, com R$ 418,80
                cobrados por ano; o mensal custa R$ 49,90, sem desconto.
                A partir de R$ 5 mil, o anual começa em R$ 50 por mês
                (R$ 600 por ano), e o mensal em R$ 70. Use o seletor e o
                simulador acima para conferir sua faixa e a economia no anual.
                Primeiro mês grátis nos dois formatos. O mensal não tem
                fidelidade, e o preço exato aparece antes de assinar.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="lp__cta-final">
        <div className="lp__interno pilha pilha-4">
          <h2 className="lp__titulo">Deixa o próximo mês{" "}
            <span className="lp__grifo">com a gente.</span></h2>
          <p className="lp__sub">
            Entre hoje e o próximo aluguel já chega cobrado, com recibo
            emitido e o imposto calculado.
          </p>
          <div className="lp__cta-linha">
            <BotaoEntrar local="final_comecar" />
            <Link to="/calculadora-imposto-aluguel" className="botao botao--secundario">
              Fazer o diagnóstico grátis
            </Link>
          </div>
        </div>
      </section>

      {modal && (
        <div className="lp__veu" onClick={() => setModal(false)}>
          <div className="lp__modal" role="dialog" aria-modal="true"
            aria-labelledby="login-titulo" aria-describedby="login-descricao" onClick={(e) => e.stopPropagation()}>
            <button className="lp__modal-fechar" aria-label="Fechar"
              onClick={() => setModal(false)}>×</button>
            <Icone tamanho={44} />
            <h2 id="login-titulo">Entrar no alugando.com</h2>
            <p className="suave" id="login-descricao">
              Acesse sua conta ou crie uma para organizar cobranças,
              recebimentos e imposto dos seus aluguéis.
            </p>
            <p className="suave lp__modal-acesso">Já tem cadastro? Use a mesma conta Google.</p>
            <button className="lp__botao-google" disabled={entrando}
              onClick={() => void entrar()}>
              <IconeGoogle tamanho={20} />
              {entrando ? "Entrando…" : "Continuar com Google"}
            </button>
            {erro && <Aviso tom="atencao">{erro}</Aviso>}
            <p className="lp__modal-oferta">
              <strong>Primeiro mês grátis.</strong>
              <span>Depois, a partir de R$ 34,90/mês no anual. Mensal sem fidelidade por R$ 49,90.</span>
            </p>
            <p className="suave lp__modal-termos">
              Ao continuar, você concorda com os{" "}
              <a href="/termos">Termos de Uso</a> e a{" "}
              <a href="/privacidade">Política de Privacidade</a>.
            </p>
          </div>
        </div>
      )}

      <footer className="lp__rodape">
        <div className="lp__interno">
          <div className="lp__rodape-grid">
            <div className="lp__rodape-marca">
              <span className="marca">
                <Icone tamanho={28} />
                <MarcaTexto invertida />
              </span>
              <p className="suave">
                Você aluga. O resto é com a gente: cobrança do inquilino,
                controle de quem pagou e o carnê-leão do aluguel, num lugar
                só, a partir de R$ 34,90 por mês no plano anual.
              </p>
              <nav className="lp__redes" aria-label="Redes sociais oficiais do Alugando">
                <a href="https://www.instagram.com/alugandooficial/" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle className="lp__rede-detalhe" cx="17.4" cy="6.7" r=".8" />
                  </svg>
                  <span>Instagram</span>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61594066443451" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3" />
                  </svg>
                  <span>Facebook</span>
                </a>
              </nav>
            </div>
            <div className="lp__rodape-col">
              <h4>Produto</h4>
              <a href="#precos">Preço</a>
              <a href="#faq">Perguntas frequentes</a>
              <Link to="/calculadora-imposto-aluguel">Diagnóstico do carnê-leão</Link>
              <Link to="/blog">Blog</Link>
              <a href="/manual/">Manual</a>
              {landingEstatica
                ? <a className="lp__rodape-link" data-cta-id="rodape_entrar" href={`${URL_APP}?entrar=1`}>Entrar</a>
                : <button className="lp__rodape-link" data-cta-id="rodape_entrar" onClick={() => { evento("cta_landing_clicado", { cta_id: "rodape_entrar", rotulo_cta: "Entrar" }); setModal(true); }}>
                    Entrar
                  </button>}
            </div>
            <div className="lp__rodape-col">
              <h4>Legal e suporte</h4>
              <Link to="/termos">Termos de uso</Link>
              <Link to="/privacidade">Política de privacidade</Link>
              <a href="mailto:suporte@alugando.com">
                suporte@alugando.com
              </a>
            </div>
          </div>

          <div className="lp__selos">
            <div>
              <strong>DARF oficial</strong>
              <small>emitido pelo Sicalc da Receita</small>
            </div>
            <div>
              <strong>Motor conferido</strong>
              <small>centavo a centavo no Carnê-Leão Web</small>
            </div>
            <div>
              <strong>Conforme LGPD</strong>
              <small>dados criptografados, encarregado nomeado</small>
            </div>
          </div>

          <p className="lp__rodape-aviso">
            O alugando.com é um software para te ajudar em cobranças,
            organização e cálculo do imposto do aluguel. O cálculo é feito a
            partir das informações fornecidas por você, que as confirma antes
            de qualquer emissão. A responsabilidade pelo tributo e pela
            declaração é do contribuinte. Até a geração da guia, valores de
            multa e juros são estimativa; o valor oficial é o do DARF emitido
            pelo Sicalc automaticamente pela plataforma.
          </p>
          <RodapeLegal soCreditos />
        </div>
        <BairroRodape />
      </footer>
    </main>
    </>
  );
}
